import React from 'react';
import type { CompanyProfileWidgetInstanceConfig, CompanyProfileData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner, formatPrice, formatMultiple } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: CompanyProfileData = {
  ticker: 'SAMPLE',
  company_name: 'Sample Corporation',
  sector: 'Technology',
  industry: 'Software',
  fy_ending: 'December',
  currency: 'USD',
  market_cap: 2850000000000,
  pe_ratio: 28.5,
};

interface Props {
  config: CompanyProfileWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const CompanyProfileWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<CompanyProfileData>(
    'company_profile',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (!displayData) return null;

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Company', value: displayData.company_name },
    { label: 'Ticker', value: displayData.ticker },
    { label: 'Sector', value: displayData.sector ?? '--' },
    { label: 'Industry', value: displayData.industry ?? '--' },
    { label: 'FY Ending', value: displayData.fy_ending ?? '--' },
    { label: 'Currency', value: displayData.currency ?? '--' },
    { label: 'Market Cap', value: formatPrice(displayData.market_cap) },
    { label: 'P/E Ratio', value: formatMultiple(displayData.pe_ratio) },
  ];

  return (
    <div className={`h-full flex flex-col overflow-hidden bg-surface ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}

      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 shrink-0">
        <div>
          <span className="text-sm font-semibold text-neutral-800">{displayData.company_name}</span>
          <span className="text-xs text-neutral-500 ml-2">{displayData.ticker}</span>
        </div>
        {displayData.market_cap != null && (
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-xs font-medium text-neutral-500 uppercase">Mkt Cap</span>
            <span className="text-sm font-semibold text-neutral-800">{formatPrice(displayData.market_cap)}</span>
          </div>
        )}
      </div>

      {/* Detail rows */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <tbody>
            {rows.map(row => (
              <tr key={row.label} className="border-b border-neutral-100">
                <td className="py-2 px-3 text-neutral-500 font-medium w-1/3">{row.label}</td>
                <td className="py-2 px-3 text-neutral-800">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyProfileWidgetRenderer;
